<script setup lang="ts">
import { ref, computed, watch, type Component } from "vue"
import PhoneOptions from '../components/PhoneOptions'
import StoreSelector from '../components/StoreSelector'
import ContactOptions from '../components/ContactOptions'
import iFrameNexor from '../components/iFrameNexor'
import WhatsAppOptions from '../components/WhatsAppOptions'
import axios from "axios";

const props = defineProps<{ metal: string, configId: string }>()
const weight = ref(0)
const isCalculating = ref(false)

const pricePerPurity = ref<Record<number, number>>({
  0: 0,
  1: 0,
  2: 0
})

const metals = {
  xau: {
    purity: ['24K', '18K', '14K'],
    label: 'oro',
    color: '#988136',
    resultDecimals: 0,
    unit: 'gr'
  },
  xag: {
    purity: ['999', '925', '800'],
    label: 'plata',
    color: '#b6b6b6',
    resultDecimals: 0,
    unit: 'kg'
  }
}

watch(weight, (newVal) => {
  pricePerPurity.value = {
    0: 0,
    1: 0,
    2: 0
  }
})

type StepKey =
  | 'options'
  | 'phone'
  | 'store'
  | 'iFrameNexor'
  | 'whatsapp'

interface StepDefinition {
  component: Component
  props?: Record<string, any>
}

interface HistoryEntry {
  key: StepKey
  props?: Record<string, any>
}

const steps: Record<StepKey, StepDefinition> = {
  options: {
    component: ContactOptions,
  },
  phone: {
    component: PhoneOptions
  },
  store: {
    component: StoreSelector
  },
  iFrameNexor: {
    component: iFrameNexor
  },
  whatsapp: {
    component: WhatsAppOptions
  }
}


// shared state between steps
const modelValue = ref<string | null>(null)

// history stack
const history = ref<HistoryEntry[]>([])
const currentEntry = computed(() => history.value.at(-1)!)
const currentStep = computed(() => currentEntry.value ? steps[currentEntry.value.key] : null)

const currentComponent = computed(() => currentStep.value?.component)


function goNext(key: StepKey, props?: Record<string, any>) {
  history.value.push({ key, props })
}

function goBack() {
  if (history.value.length > 0) {
    history.value.pop()
  }
}

const config = computed(() => {
  const m = props.metal as 'xau' | 'xag'
  return metals[m] || metals.xau
})

async function calculate() {
  isCalculating.value = true

  const baseUrl = 'https://capable-heliotrope-e954ce.netlify.app';
  const { data } = await axios.get(`${baseUrl}/.netlify/functions/get-price`, {
    params: {
      metal: props.metal,
      weight: weight.value
    }
  })

  isCalculating.value = false

  if (data.error) {
    console.error('Error getting the price')
    return
  }

  pricePerPurity.value = {
    0: Number(data.purities[config.value.purity[0]].totalPrice),
    1: Number(data.purities[config.value.purity[1]].totalPrice),
    2: Number(data.purities[config.value.purity[2]].totalPrice)
  }

  goNext('options', {
    configId: props.configId
  })
}
</script>

<template>
  <div class="max-w-2xl mx-auto rounded-xl sm:rounded-2xl bg-white p-4 sm:p-6 shadow-lg">
    <!-- Title -->
    <h2 class="text-center text-lg font-semibold text-gray-600 uppercase">
      *CALCULA EL VALOR DE
      <span class="underline" :style="`color: ${config.color}; text-decoration-color: ${config.color}`">{{ `TU ${config.label}` }}</span>
    </h2>

    <!-- Calculator -->
    <div class="hidden md:grid mt-6 grid grid-cols-4 gap-4 items-end">
      <!-- Peso -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">
          Peso ({{ config.unit }})
        </label>
        <input
          type="number"
          v-model.number="weight"
          class="w-full rounded-lg border border-solid border-gray-400 px-3 py-2 text-center text-base focus:outline-none focus:ring-2 focus:ring-yellow-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </div>

      <div v-for="(purity, index) in config.purity" class="text-center">
        <p class="text-xs text-gray-500 mb-1">{{ purity }}</p>
        <div class="rounded-lg border border-solid border-gray-300 bg-gray-50 py-2 text-base font-semibold text-gray-700">
          {{ pricePerPurity[index] === 0 ? '&nbsp;' : `${pricePerPurity[index].toFixed(config.resultDecimals)}€` }}
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 md:hidden">
      <!-- Peso -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">
          Peso ({{ config.unit }})
        </label>
        <input
          type="number"
          v-model.number="weight"
          class="w-full rounded-lg border border-solid border-gray-400 px-3 py-2 text-center text-base focus:outline-none focus:ring-2 focus:ring-yellow-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </div>

      <!-- Button -->
      <button
        class="mt-5 rounded-lg inline-flex bg-yellow-400 py-2 text-base font-semibold text-gray-700 hover:bg-yellow-500 transition justify-center items-center disabled:bg-gray-200"
        @click="calculate"
        :disabled="isCalculating || weight === 0"
      >
        <svg v-if="isCalculating" class="mr-3 -ml-1 size-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        CALCULAR
      </button>
    </div>


    <div class="md:hidden grid mt-4 grid grid-cols-3 gap-2 items-end">
      <div v-for="(purity, index) in config.purity" class="text-center">
        <p class="text-xs text-gray-500 mb-1">{{ purity }}</p>
        <div class="rounded-lg border border-solid border-gray-300 bg-gray-50 py-2 text-base font-semibold text-gray-700">
          {{ pricePerPurity[index] === 0 ? '&nbsp;' : `${pricePerPurity[index].toFixed(config.resultDecimals)}€` }}
        </div>
      </div>
    </div>

    <!-- Button -->
    <button
      class="hidden md:inline-flex mt-4 rounded-lg bg-yellow-400 px-6 py-2 text-base font-semibold text-gray-700 hover:bg-yellow-500 transition items-center disabled:bg-gray-200"
      @click="calculate"
      :disabled="isCalculating || weight === 0"
    >
      <svg v-if="isCalculating" class="mr-3 -ml-1 size-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
      CALCULAR
    </button>

    <div>
      <!-- Subtitle -->
      <h3 class="mt-8 text-center text-base font-semibold text-gray-600" v-show="history.length">
        PARA SEGUIR ADELANTE SELECCIONA CÓMO QUIERES CONTACTARNOS
      </h3>

      <!-- Options -->
      <div class="mt-4 rounded-2xl bg-gray-100 md:p-4 p-2" v-show="history.length">
        <!-- Back -->
        <button
          v-if="currentEntry && currentEntry.key !== 'options'"
          type="button"
          class="mb-6 flex items-center text-gray-600 hover:text-gray-800"
          @click="goBack"
        >
          <span class="mr-2 text-lg">←</span>
          <span class="text-sm">Elegir otra forma</span>
        </button>

        <Component
          v-if="history.length"
          :is="currentComponent"
          v-bind="currentEntry.props"
          @back="goBack"
          @next="goNext"
          v-model="modelValue"
        />

        <p class="mt-6 text-xs text-gray-500 text-center leading-relaxed">
          El precio que aparece en la calculadora es el precio máximo según la cotización del dia, el precio final oscila según la cantidad total que venda, el precio de la calculadora no incluye el % del impuesto ITP que habrá que descontar del valor final en tienda que oscila entre un 4,5 y un 5%
        </p>

      </div>
    </div>
  </div>
</template>
